import { useEffect } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// 20 ausführliche, Geo-/AI-SEO-optimierte FAQ-Einträge für MediaFly Production.
// Antworten sind bewusst lang (3–5 Sätze) für bessere Indexierung in
// Google AI Overviews, Perplexity, ChatGPT etc.
const FAQS = [
  {
    q: "Wie läuft eine Reel-Produktion bei MediaFly Production in Frankfurt ab?",
    a: "Nach der ersten Anfrage über WhatsApp, Telefon oder Formular besprechen wir Ziel, Zielgruppe und Plattform (Instagram, TikTok, LinkedIn). Anschließend folgt ein kurzer Vorbereitungs-Call mit Hook-Ideen, danach kommen wir mit professionellem Equipment direkt zu dir nach Frankfurt oder ins Rhein-Main-Gebiet. An nur einem Drehtag entstehen je nach Paket 5, 10 oder 20 fertig geschnittene Reels – inklusive Cuts, Sounddesign und ersten Performance-Hooks.",
  },
  {
    q: "Wie schnell bekomme ich meinen fertigen Social-Media-Content?",
    a: "Die reguläre Lieferzeit beträgt 7–14 Werktage nach dem Drehtag. Für zeitkritische Kampagnen wie Produkt-Launches oder Black-Friday-Aktionen liefern wir auf Anfrage auch in unter 5 Werktagen – sag uns einfach im Erstgespräch, wann du den Content brauchst. Jedes Reel wird im 9:16-Hochformat exportiert, optimiert für Instagram Reels, TikTok und YouTube Shorts.",
  },
  {
    q: "Produziert ihr auch TikTok-Content und Instagram Reels?",
    a: "Ja, der gesamte Content wird nativ im 9:16-Vertikalformat produziert und ist 1:1 für Instagram Reels, TikTok, YouTube Shorts und Facebook Reels einsetzbar. Wir schneiden plattformspezifisch: schnellere Cuts und prägnante Hooks für TikTok, etwas längere Storytelling-Reels für Instagram. Auf Wunsch liefern wir zusätzliche Varianten in 1:1 für Feed-Posts oder 16:9 für YouTube-Pre-Rolls.",
  },
  {
    q: "Arbeitet ihr im gesamten Rhein-Main-Gebiet?",
    a: "Ja. Unser Einzugsgebiet umfasst Frankfurt am Main, Offenbach, Hanau, Aschaffenburg, Darmstadt, Wiesbaden, Mainz, Bad Homburg und das gesamte Rhein-Main-Gebiet. Innerhalb dieses Radius drehen wir ohne Anfahrtspauschale. Für Produktionen außerhalb (z.B. Bundesweit) bieten wir gesonderte Reise-Pakete an – sprich uns dazu einfach im Erstgespräch an.",
  },
  {
    q: "Könnt ihr direkt Content für Meta Ads und Instagram Ads filmen?",
    a: "Ja, das ist sogar einer unserer Schwerpunkte. Wir produzieren Reels mit Performance-Hooks in den ersten 2 Sekunden, kurzen Cuts und klaren CTAs – exakt so, wie Meta Ads heute ausgespielt werden müssen, um zu konvertieren. Du erhältst sofort einsetzbare Anzeigen-Creatives, die du im Meta Business Manager hochladen kannst, inklusive verschiedener Hook-Varianten für A/B-Tests.",
  },
  {
    q: "Unterstützt ihr auch Unternehmen in Aschaffenburg, Darmstadt oder Hanau?",
    a: "Ja. Wir arbeiten regelmäßig mit Unternehmen, Coaches, Fitnessstudios und Restaurants in Aschaffenburg, Darmstadt, Hanau, Offenbach, Wiesbaden und Mainz zusammen. Da wir unseren Sitz in Babenhausen haben (Landkreis Darmstadt-Dieburg), sind wir in unter 30 Minuten in den meisten Rhein-Main-Städten vor Ort.",
  },
  {
    q: "Welche Unternehmen buchen eure Videoproduktion?",
    a: "Wir produzieren Content für Fitnessstudios und Personal Trainer, Kampfsport-Studios, Coaches und Berater, Restaurants und Gastronomie, lokale Dienstleister, Handwerksbetriebe, B2B-Unternehmen sowie Marken im E-Commerce. Besonders gefragt sind wir bei Frankfurter Gyms, Aschaffenburger Restaurants und Coaches im gesamten Rhein-Main-Gebiet, die mit organischem Reel-Content und Meta Ads skalieren wollen.",
  },
  {
    q: "Was kostet eine professionelle Reel-Produktion bei euch?",
    a: "Unsere Mindestabnahme liegt bei 5 Reels für 40 € netto pro Reel. Ab 10 Reels gewähren wir 5 % Rabatt, ab 20 Reels 15 %. Drohnen-Aufnahmen sind als Add-on für +30 € netto pro Einsatz buchbar (haftpflichtversichert). Alle Preise sind transparent auf der Seite einsehbar – es gibt keine versteckten Kosten oder Set-up-Gebühren.",
  },
  {
    q: "Warum funktionieren authentische Reels oft besser als klassische Werbevideos?",
    a: "Klassische Werbevideos werden in Social Feeds sofort als Werbung erkannt und übersprungen – die Watchtime liegt häufig unter 3 Sekunden. Authentische, im Reel-Stil produzierte Inhalte fügen sich nahtlos in den organischen Feed ein, erzielen 3–5× höhere Watchtime und damit deutlich günstigere Klickpreise in Meta Ads. Genau dieser Native-Look ist unser Spezialgebiet.",
  },
  {
    q: "Produziert ihr auch Behind-the-Scenes-Content und Vlogs?",
    a: "Ja, Behind-the-Scenes (BTS) ist eines unserer stärksten Formate. BTS-Reels zeigen die Menschen hinter der Marke, schaffen Vertrauen und generieren überdurchschnittlich viele Speichern- und Teilen-Interaktionen. Wir begleiten Drehs, Events, Trainings oder einen ganz normalen Arbeitstag – und schneiden daraus 5–20 hochwertige Reels.",
  },
  {
    q: "Könnt ihr mehrere Videos an einem einzigen Drehtag produzieren?",
    a: "Ja, das ist sogar der Standard-Workflow. An einem effizient geplanten Drehtag von 4–8 Stunden produzieren wir je nach Paket 5, 10 oder 20 vertikale Reels. Durch Outfit-Wechsel, mehrere Locations und vorbereitete Hooks entsteht so über einen einzigen Tag Content für mehrere Wochen Social-Media-Präsenz.",
  },
  {
    q: "Unterstützt ihr bei Hooks, Skripten und Content-Ideen?",
    a: "Ja, Hook-Beratung ist in jedem Paket inkludiert. Vor jedem Drehtag bereiten wir gemeinsam mit dir 10–30 plattformgerechte Hook-Varianten vor, basierend auf aktuellen Trends auf TikTok und Instagram. Bei den Paketen ab 10 Reels gehört außerdem eine strategische Content-Plan-Beratung dazu – damit du nicht nur Videos hast, sondern eine Serie, die performt.",
  },
  {
    q: "Kann man den Content direkt für Meta Ads, Google Ads und TikTok Ads verwenden?",
    a: "Ja, alle gelieferten Reels sind plattformneutral und sofort als Werbeanzeige einsetzbar. Wir liefern die Videos im MP4-H.264-Format mit den korrekten Spezifikationen für den Meta Ads Manager, TikTok Ads Manager und Google Ads Video Campaigns. Auf Wunsch liefern wir auch reduzierte Untertitel-Versionen für Sound-Off-Wiedergabe.",
  },
  {
    q: "Wie wichtig ist vertikales 9:16-Video für Social Media heute?",
    a: "9:16 ist heute Pflicht: Instagram Reels, TikTok, YouTube Shorts, Facebook Reels und LinkedIn Video bevorzugen oder erzwingen das Hochformat. Vertikale Videos belegen den gesamten Bildschirm auf Smartphones und erreichen nachweislich höhere Watchtime und Engagement-Raten. Wir produzieren daher konsequent nativ in 9:16 – kein nachträgliches Cropping aus 16:9.",
  },
  {
    q: "Produziert ihr auch für Fitnessstudios, Kampfsport-Studios und Gyms?",
    a: "Ja, das ist einer unserer stärksten Branchen-Schwerpunkte. Wir produzieren regelmäßig Trainings-Reels, Mitglieder-Testimonials, Outdoor-Workouts und Atmosphären-Cuts für Fitnessstudios und Kampfsport-Gyms in Frankfurt, Aschaffenburg und Darmstadt. Unsere Reels zeigen Intensität, Community und Erfolge – genau das, was neue Mitglieder auf Instagram und TikTok überzeugt.",
  },
  {
    q: "Dreht ihr auch auf Events, Messen und Firmenveranstaltungen?",
    a: "Ja. Wir produzieren Event-Reels für Firmenveranstaltungen, Messen, Produkt-Launches, Eröffnungen und private Business-Events im gesamten Rhein-Main-Gebiet. Du erhältst nicht nur eine Aftermovie-Zusammenfassung, sondern eine ganze Serie an 5–20 kurzen Reels, die du in den Wochen nach dem Event auf allen Kanälen ausspielen kannst.",
  },
  {
    q: "Wie läuft die Zusammenarbeit konkret ab – von Anfrage bis Lieferung?",
    a: "1) Anfrage per WhatsApp/Telefon/Formular. 2) Kostenloses Erstgespräch (15 Min) zur Klärung von Zielen und Budget. 3) Angebot und Termin-Fixierung. 4) Hook- und Content-Briefing 3–5 Tage vor dem Dreh. 5) Drehtag bei dir vor Ort (4–8 h). 6) Schnitt und Sounddesign. 7) Lieferung der Reels mit 2 Korrekturschleifen inklusive. 8) Optionaler Performance-Check nach 4 Wochen.",
  },
  {
    q: "Welche Kamera- und Equipment-Qualität nutzt ihr beim Dreh?",
    a: "Wir drehen mit professionellem Hybrid-Equipment: spiegellosen Vollformat-Kameras (4K, log-fähig), Cinema-Objektiven, Gimbals für ruhige Bewegungsaufnahmen, Funkstrecken-Mikrofonen für sauberen O-Ton und LED-Continuous-Lights. Für Drohnen-Aufnahmen nutzen wir versicherte DJI-Drohnen. Das Setup ist bewusst kompakt, damit du nicht das Gefühl hast, ein riesiges Filmteam stehe vor dir – sondern eher ein erfahrener Creator mit High-End-Equipment.",
  },
  {
    q: "Wie schnell kann ich mit der Zusammenarbeit starten?",
    a: "In der Regel können wir innerhalb von 5–10 Werktagen den ersten Drehtag realisieren – bei kurzfristigen Anfragen auch früher. Schreib uns einfach kurz auf WhatsApp (wa.me/491704444355), was du brauchst, dann bekommst du in der Regel innerhalb von 2 Stunden eine Antwort und einen Vorschlag für die nächsten Termine.",
  },
  {
    q: "Gibt es feste Pakete oder auch individuelle Content-Produktionen?",
    a: "Wir bieten standardmäßig 3 transparente Pakete an (5, 10 oder 20 Reels) – das deckt 90 % aller Anfragen ab und macht die Zusammenarbeit für dich kalkulierbar. Für umfangreichere Projekte (z.B. monatlicher Content-Retainer, Multi-Day-Shoots, Eventreihen oder ganze Kampagnen-Konzepte) erstellen wir individuelle Angebote. Sprich uns einfach an, dann besprechen wir den passenden Rahmen.",
  },
];

export default function FAQ() {
  // Inject FAQPage JSON-LD schema for SEO / AI search engines
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "mfp-faq-schema";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById("mfp-faq-schema");
      if (existing) existing.remove();
    };
  }, []);

  return (
    <section className="border-b border-white/10" data-testid="faq-section" id="faq">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-14 md:py-24">
        <div className="mb-10 md:mb-14">
          <div className="text-[11px] uppercase tracking-[0.3em] text-mfp-gold font-bold mb-3">
            FAQ
          </div>
          <h2 className="font-display text-3xl md:text-5xl uppercase leading-[1.05]">
            Häufige <span className="text-mfp-gold">Fragen</span>
          </h2>
          <p className="mt-4 text-mfp-muted text-base md:text-lg max-w-2xl">
            Reel-Produktion, Videograf-Leistungen und Social-Media-Content in
            Frankfurt, Aschaffenburg, Darmstadt, Offenbach, Hanau, Wiesbaden,
            Mainz und dem gesamten Rhein-Main-Gebiet.
          </p>
        </div>

        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQS.map((item, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="border-b border-white/10"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger
                className="text-left text-base md:text-lg font-display uppercase tracking-tight py-5 md:py-6 hover:no-underline text-white hover:text-mfp-gold"
                data-testid={`faq-trigger-${i}`}
              >
                <h3 className="text-left font-display uppercase tracking-tight text-base md:text-lg">
                  {item.q}
                </h3>
              </AccordionTrigger>
              <AccordionContent
                className="text-mfp-muted text-sm md:text-base leading-relaxed pb-6"
                data-testid={`faq-content-${i}`}
              >
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <p className="mt-10 text-center text-sm text-mfp-muted">
          Deine Frage ist nicht dabei?{" "}
          <a
            href="https://wa.me/491704444355"
            target="_blank"
            rel="noopener noreferrer"
            className="text-mfp-gold font-bold hover:underline"
            data-testid="faq-whatsapp-link"
          >
            Schreib uns kurz auf WhatsApp
          </a>{" "}
          – Antwort in der Regel innerhalb von 2 Stunden.
        </p>
      </div>
    </section>
  );
}
